import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Typography,
  TableContainer,
  Button,
  TableSortLabel,
  Box,
  useTheme,
  Tooltip,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { useState, useMemo } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

interface KeywordDetailsPopupProps {
  open: boolean;
  onClose: () => void;
  keywordData: any[] | null | undefined;
}

interface KeywordRow {
  keyword_name: string;
  click_in: number;
  ftd: number;
  current_qs: number | null;
  current_lp_exp: string;
}

type Order = "asc" | "desc";

interface HeadCell {
  id: keyof KeywordRow;
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  { id: "keyword_name", label: "Keyword", numeric: false },
  { id: "click_in", label: "Clicks", numeric: true },
  { id: "ftd", label: "FTD", numeric: true },
  { id: "current_qs", label: "QS", numeric: true },
  { id: "current_lp_exp", label: "LP Experience", numeric: false },
] as const;

export const KeywordDetailsPopup = ({
  open,
  onClose,
  keywordData,
}: KeywordDetailsPopupProps) => {
  const theme = useTheme();
  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState("");
  const infoText = "  from Paid Traffic in the last 30 Days";

  const handleRequestSort = (property: keyof KeywordRow) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = useMemo(() => {
    if (!keywordData) return [];
    return [...keywordData].sort((a, b) => {
      if (orderBy === "keyword_name" || orderBy === "current_lp_exp") {
        return order === "asc"
          ? a[orderBy].localeCompare(b[orderBy])
          : b[orderBy].localeCompare(a[orderBy]);
      } else {
        // Handle null values in sorting
        const aValue = a[orderBy] ?? 0;
        const bValue = b[orderBy] ?? 0;
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }
    });
  }, [keywordData, order, orderBy]);

  const createSortHandler = (property: keyof KeywordRow) => () => {
    handleRequestSort(property);
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== "backdropClick") {
          onClose();
        }
      }}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        style: {
          minHeight: "400px",
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">Keywords Leading To This Page</Typography>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          flex: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          padding: 0,
        }}
      >
        <TableContainer
          component={Paper}
          sx={{
            flex: 1,
            overflow: "auto",
            borderRadius: 0,
          }}
        >
          <Table size="small" aria-label="keyword details table" stickyHeader>
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    align={"left"}
                    sortDirection={orderBy === headCell.id ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : "asc"}
                      onClick={createSortHandler(headCell.id)}
                    >
                      {headCell.label}
                      {orderBy === headCell.id ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </Box>
                      ) : null}

                      <Tooltip title={headCell.label + infoText}>
                        <InfoOutlinedIcon
                          fontSize="small"
                          sx={{
                            ml: 0.5,
                            verticalAlign: "middle",
                            color: "grey.600",
                          }}
                        />
                      </Tooltip>
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData && sortedData.length > 0 ? (
                sortedData.map((row: KeywordRow, index: number) => (
                  <TableRow key={index} hover>
                    <TableCell align="left">{row.keyword_name}</TableCell>
                    <TableCell align="left">
                      {row.click_in?.toLocaleString()}
                    </TableCell>
                    <TableCell align="left">
                      {row.ftd?.toLocaleString()}
                    </TableCell>
                    <TableCell align="left">
                      {row.current_qs != null ? `${row.current_qs}` : "-"}
                    </TableCell>
                    <TableCell align="left">
                      {row.current_lp_exp || "-"}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={headCells.length}
                    align="center"
                    sx={{ py: 4 }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      No keyword data available
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            padding: 2,
            backgroundColor: theme.palette.background.paper,
            position: "sticky",
            bottom: 0,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button
            variant="contained"
            color="secondary"
            onClick={onClose}
            sx={{
              minWidth: 120,
            }}
          >
            CLOSE
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
